import {
  getDataSourceNumericId,
  getLastDataSourceTest,
  runDataSourceTest,
} from 'DataSources/datasourcesApi';

export const sourceEditorPath = (id) => '/sources/source-editor/' + encodeURIComponent(id);

/**
 * Pre-run connection gate — every discover / profile / masking start calls this first.
 *
 * A data source is cleared to run when it has a successful connection test on record.
 * When it doesn't, a fresh test is run right here; only if that fresh test also fails
 * is the start blocked. On block: the user is notified and routed to the data source
 * editor so they can fix the connection (pass navigateOnFail: false to suppress the
 * redirect — e.g. bulk starts, or callers already on the editor).
 *
 * Accepts either a UI data-source row or a bare id. Returns { ok, message? }; a source
 * whose id can't be resolved passes through un-gated so the start call itself reports
 * the real error.
 */
export async function ensureConnectionTested(sourceOrId, opts = {}) {
  const { history, setNotification, navigateOnFail = true, name } = opts;

  let row = null;
  let id = null;
  if (sourceOrId != null && typeof sourceOrId === 'object') {
    row = sourceOrId;
    id = getDataSourceNumericId(row);
    if (id == null && row.id != null && Number.isFinite(Number(row.id))) id = Number(row.id);
  } else if (sourceOrId != null && Number.isFinite(Number(sourceOrId))) {
    id = Number(sourceOrId);
  }
  if (id == null) return { ok: true };

  // A success the caller's row already carries (server-derived) short-circuits the fetch.
  if (row && row.lastTestSuccess === true) return { ok: true };

  try {
    const last = await getLastDataSourceTest(id);
    if (last && last.tested === true && last.success === true) return { ok: true };
  } catch (e) {
    // No recorded test (or lookup failed) — fall through and run one now.
  }

  let message;
  try {
    const result = await runDataSourceTest(id);
    if (result && result.success === true) return { ok: true };
    message = (result && (result.error || result.message)) || 'Connection test failed.';
  } catch (e) {
    message = (e && e.message) || 'Connection test failed.';
  }

  const label = name || (row && row.name) || `data source #${id}`;
  if (typeof setNotification === 'function') {
    setNotification({
      header: 'Connection test failed',
      body: `Cannot start the run for ${label}: ${message} Fix the connection details and try again.`,
    });
  }
  if (navigateOnFail && history) {
    history.push(sourceEditorPath(id));
  }
  return { ok: false, message };
}
